import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { formatDateSafe } from '@/shared/formatSafe';
import { I18nContext } from './context';
import { persistLocale, readLocale } from './localeStorage';
import { translations } from './translations';
import type { Locale, TranslationKey, TranslateParams } from './types';

function readInitialLocale(): Locale {
  return readLocale();
}

function interpolate(template: string, params?: TranslateParams) {
  if (!params) return template;
  return Object.entries(params).reduce(
    (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
    template,
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale);
    persistLocale(nextLocale);
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: TranslateParams) => {
      const template = translations[locale][key] ?? translations.en[key] ?? key;
      return interpolate(template, params);
    },
    [locale],
  );

  const formatDate = useCallback(
    (value: string | Date) => formatDateSafe(value, locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    [locale],
  );

  const formatDateTime = useCallback(
    (value: string | Date) => formatDateSafe(value, locale, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, formatDate, formatDateTime }),
    [formatDate, formatDateTime, locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
